import React, { useEffect, useState } from "react";
import { Divider, Collapse, Row, Col } from "antd";
import type { Moment } from "moment";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "../../..";
import { ReactComponent as DoubleEyeOpenIcon } from "../../../img/double-eye-open.svg";
import { ReactComponent as DoubleEyeClosedIcon } from "../../../img/double-eye-closed.svg";
import { GroupOffer } from "../../../components/OfferDetails/GroupOffer/GroupOffer";
import {
  closeOfferHierarchy,
  createOfferHierarchy,
  deleteOfferHierarchy,
  fetchCurrentOfferHierarchy,
  fetchCurrentOfferHierarchyVersions,
  offeringActions,
} from "../../../store/slices/offering/offering";
import { Offer } from "../../../lib/types";
import { toHttpFormatEndOfDay } from "../../../lib/utils";
import type { IBodyCreateHierarchy, IOfferHierarchy } from "../../../store/slices/offering/types";
import { useOfferContext } from "../context/OfferContext";
import HistoryAndPropertyModal from "../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { hasAccess } from "../../../components/RolesModule/hasAccess";
import { fetchOfferVersionsByIdRoute } from "../../../lib/routes/offering";
import { MENU_ADMIN_OFFERING_WRITE } from "../../../lib/const/permissions";
import { useOfferList } from "../../../components/OfferList/utils/useOfferList";
import { IChangingRecordKey } from "../../../lib/types/IViewMode";
import "./GroupOfferContainer.less";

export const GroupOfferContainer = () => {
  const { initialSearchValues, activeOfferVersion } = useOfferContext();

  const dispatch = useAppDispatch();
  const [groupTreeExpandedKeys, setGroupTreeExpandedKeys] = useState<string[]>([]);

  const offerListProps = useOfferList({
    initialOfferListViewMode: "selectOffer",
    initialSearchValues,
  });

  // Загрузка иерархии предложения
  useEffect(() => {
    if (activeOfferVersion?.id) {
      dispatch(
        fetchCurrentOfferHierarchy({ id: activeOfferVersion.id, onDate: activeOfferVersion.toDate })
      );
      dispatch(fetchCurrentOfferHierarchyVersions(activeOfferVersion.id));
    }
    return () => {
      dispatch(offeringActions.cleanOfferHierarchy());
      dispatch(offeringActions.cleanOfferHierarchyVersions());
    };
  }, [activeOfferVersion]);

  // Добавляемый в дерево оффер
  const [addedOffer, setAddedOffer] = useState<IOfferHierarchy | Offer>();
  const [addedOfferVersions, setAddedOfferVersions] = useState<Offer[]>();

  // Запрос версий добавленного предложения для использования в дизейбле дат
  useEffect(() => {
    if (addedOffer) {
      axios.get(fetchOfferVersionsByIdRoute(addedOffer.id!)).then(({ data }) => {
        setAddedOfferVersions(data);
      });
    } else {
      setAddedOfferVersions(undefined);
    }
  }, [addedOffer]);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  // [ключ редактируемой строки, mode]
  const [changingRecordKey, setChangingRecordKey] = useState<IChangingRecordKey>();

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  const [isVersionsOpen, setIsVersionsOpen] = useState<boolean>(false);
  const [isCollapseOpen, setIsCollapseOpen] = useState<boolean>(true);

  const toggleCollapse = () => {
    setIsCollapseOpen((prev) => !prev);
  };

  const onClickVersionIcon = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    if (!activeOfferVersion?.id) {
      return;
    }
    if (isVersionsOpen) {
      setIsVersionsOpen(false);
    } else {
      dispatch(fetchCurrentOfferHierarchyVersions(activeOfferVersion.id));
      setIsVersionsOpen(true);
    }
  };

  const submitCreateOfferHierarchy = (params: { id: number; body: IBodyCreateHierarchy }) => {
    dispatch(createOfferHierarchy(params)).then(() => {
      setChangingRecordKey(undefined);
      dispatch(fetchCurrentOfferHierarchy({ id: params.id }));
      dispatch(fetchCurrentOfferHierarchyVersions(params.id));
      setAddedOffer(undefined);
    });
  };

  const handleCloseOfferHierarchy = ({
    fromDate,
    offeringLinkIds,
  }: {
    offeringLinkIds: number[];
    fromDate: Moment;
  }) => {
    dispatch(
      closeOfferHierarchy({
        fromDate: toHttpFormatEndOfDay(fromDate),
        offeringLinkIds,
      })
    ).then(() => {
      setChangingRecordKey(undefined);
      dispatch(fetchCurrentOfferHierarchy({ id: activeOfferVersion?.id! }));
      dispatch(fetchCurrentOfferHierarchyVersions(activeOfferVersion?.id!));
    });
  };

  const handleDeleteOfferHierarchy = (offeringLinkIds: number[]) => {
    dispatch(deleteOfferHierarchy(offeringLinkIds)).then(() => {
      setChangingRecordKey(undefined);
      dispatch(fetchCurrentOfferHierarchy({ id: activeOfferVersion?.id! }));
      dispatch(fetchCurrentOfferHierarchyVersions(activeOfferVersion?.id!));
    });
  };

  if (activeOfferVersion?.bundleTypeId === 1) return null;

  return (
    <div className="group-offer-container">
      <Divider className="group-offer-container__upperDivider" />
      <Collapse activeKey={isCollapseOpen ? ["1"] : []} bordered={false} onChange={toggleCollapse}>
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="group-offer-container__pageTitle">
              <h3 id="group-offering-title">Групповое предложение</h3>
              {isCollapseOpen && (
                <Row align="middle" justify="end" gutter={7}>
                  {!changingRecordKey && (
                    <Col>
                      {isVersionsOpen && <DoubleEyeOpenIcon onClick={onClickVersionIcon} />}
                      {!isVersionsOpen && <DoubleEyeClosedIcon onClick={onClickVersionIcon} />}
                    </Col>
                  )}
                </Row>
              )}
            </Row>
          }
        >
          <Divider style={{ margin: 0 }} />
          <GroupOffer
            addedOffer={addedOffer}
            addedOfferVersions={addedOfferVersions}
            setAddedOffer={setAddedOffer}
            changingRecordKey={changingRecordKey}
            setChangingRecordKey={setChangingRecordKey}
            submitCreateOfferHierarchy={submitCreateOfferHierarchy}
            handleCloseOfferHierarchy={handleCloseOfferHierarchy}
            handleDeleteOfferHierarchy={handleDeleteOfferHierarchy}
            showHistoryModal={showHistoryModal}
            offerListProps={offerListProps}
            groupTreeExpandedKeys={groupTreeExpandedKeys}
            setGroupTreeExpandedKeys={setGroupTreeExpandedKeys}
            isVersionsOpen={isVersionsOpen}
            activeOfferVersion={activeOfferVersion}
          />
        </Collapse.Panel>
      </Collapse>
      <HistoryAndPropertyModal
        objectTypeSysname="offering_link"
        objectId={objectId}
        handleHideModal={() => {
          setHistoryModalVisible(false);
        }}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_OFFERING_WRITE)}
      />
    </div>
  );
};
